import React, {useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {useSelector} from "react-redux";
import {useQueryClient} from "@tanstack/react-query";
import {GLTFExporter} from "three/examples/jsm/exporters/GLTFExporter";
import {generateUUID} from "three/src/math/MathUtils";
import {ASSET_TYPE, MIME_TYPE, MY_ASSET_CATEGORY} from "utils/constants/assets";
import {useCreateAssetsQuery} from "hooks";
import {queryKeys} from "utils";
import {uploadAsset, uploadAssetThumbnail} from "api-v3";
import TagClose from "images/library/tag_close.svg";
import thumbnailInputImg from "images/library/input_thumbnail_button.png";
import styles from "./index.module.scss";
import ProgressBox from "components/Canvas/ProgressBox";

function SaveAsAssetPopup({dismiss}) {
  const intl = useIntl();
  const thumbnailRef = useRef();
  const keywordInputRef = useRef();
  const exporter = new GLTFExporter();
  const queryClient = useQueryClient();
  const createMutation = useCreateAssetsQuery();
  const selectedObject = useSelector(state => state.asset.selectedObjects[0]);

  const [thumbnailPreview, setThumbnailPreview] = useState();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [keywordList, setKeywordList] = useState([]);
  const [thumbnail, setThumbnail] = useState();
  const [keyword, setKeyword] = useState("");
  const [name, setName] = useState("");
  // const [isPublic] = useState(false);

  const isValid = thumbnail && name.length && keywordList.length;
  const INITIAL_UPLOAD_PROGRESS = 10;

  const onChangeThumbnail = file => {
    setThumbnail(file);
    setThumbnailPreview(window.URL.createObjectURL(file));
  };

  const onClickCancel = () => {
    dismiss();
  };

  const updateName = e => {
    setName(e.target.value);
  };

  const onChangeKeyword = e => {
    setKeyword(e.target.value);
  };

  const onKeydownKeyword = e => {
    if ((e.target.value.length !== 0 && e.key === " ") || e.key === "Enter") {
      if (keywordList.length <= 10) {
        createKeyword(e);
      }
    }
    if (e.target.value.length === 0 && e.key === "Backspace") {
      onKeyDownDeleteKeyword(e);
    }
  };

  const createKeyword = e => {
    if (keywordList.includes(keyword)) {
      const filteredList = keywordList.filter(keywordItem => {
        return keywordItem !== keyword;
      });
      filteredList.push(keyword);
      setKeywordList(filteredList);
    } else {
      const updatedKeywordList = [...keywordList];
      updatedKeywordList.push(keyword);
      setKeywordList(updatedKeywordList);
    }
    setKeyword("");
  };

  const onClickDeleteKeyword = selectedKeyword => {
    const filteredList = keywordList.filter(keywordItem => {
      return keywordItem !== selectedKeyword;
    });
    setKeywordList(filteredList);
  };

  const onKeyDownDeleteKeyword = () => {
    const updatedKeywordList = [...keywordList];
    updatedKeywordList.pop();
    setKeywordList(updatedKeywordList);
  };

  const focusKeywordInput = () => {
    keywordInputRef.current.focus();
  };

  const saveAsset = async () => {
    const assetUUID = generateUUID();
    const thumbnailResponse = await uploadAssetThumbnail({filename: assetUUID});
    const assetResponse = await uploadAsset({filename: assetUUID});
    const urlData = {thumbnail: thumbnailResponse.data, asset: assetResponse.data};

    try {
      await uploadAssetHandler(assetUUID, urlData);
    } catch (err) {
      //TODO: add error exception
    }
  };

  const uploadAssetHandler = async (assetUUID, urlData) => {
    exporter.parse(
      selectedObject,
      async gltf => {
        const jsonString = JSON.stringify(gltf);
        const asset = {
          uuid: assetUUID,
          model: jsonString,
          thumbnail,
        };
        const size = new TextEncoder().encode(jsonString).length;
        if (size >= Math.pow(10, 7)) return;

        const body = {
          url: urlData.asset.downloadUrl,
          thumbnailUrl: urlData.thumbnail.downloadUrl,
          topCategory: ASSET_TYPE.MODEL,
          mimetype: MIME_TYPE.GLTF,
          name: name,
          keyword: keywordList,
          size: formatFileSize(size),
        };

        await uploadAssetToS3(asset.model, urlData.asset);
        await uploadAssetToS3(asset.thumbnail, urlData.thumbnail);

        createMutation.mutateAsync(body, {
          onSuccess: () => {
            queryClient.invalidateQueries(queryKeys.allAssetKey);
          },
        });
      },
      {
        function(error) {
          //Todo: 예외처리
        },
      },
    );
  };

  const uploadAssetToS3 = (file, urlData) => {
    return new Promise(async (resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open("PUT", urlData.uploadUrl, true);

      xhr.upload.addEventListener("progress", event => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          if (progress === 100) {
            resolve();
          }
        }
      });

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.responseText);
        } else {
          reject(new Error("Asset upload failed"));
        }
      };

      xhr.onerror = () => {
        reject(new Error("Asset upload failed"));
      };

      xhr.send(file);
    });
  };

  const formatFileSize = bytes => {
    if (bytes >= Math.pow(10, 6)) {
      return (bytes / Math.pow(10, 6)).toFixed(1) + "M";
    } else if (bytes >= Math.pow(10, 3)) {
      return (bytes / Math.pow(10, 3)).toFixed(1) + "K";
    } else {
      return bytes + "B";
    }
  };

  const onClickUpload = () => {
    saveAsset();
    dismiss();
  };

  return (
    <>
      <div className={styles.savs_asset_popup}>
        <h3 className={styles.title}>
          <FormattedMessage id="ID_SAVE_AS_ASSET_POPUP_TITLE" />
        </h3>
        <div className={styles.inner}>
          <input
            id="asset_thumbnail_input"
            className={styles.asset_thumbnail_input}
            ref={thumbnailRef}
            type="file"
            onChange={e => onChangeThumbnail(e.target.files[0])}
          />
          <img
            src={thumbnailPreview || thumbnailInputImg}
            className={styles.thumbnail_input_button}
            onClick={() => thumbnailRef.current.click()}
            alt="preview of saving asset"
          />

          <div className={styles.info_wrap}>
            <div className={styles.flex_row}>
              <span className={styles.category}>
                <FormattedMessage id="ID_CATEGORY_NAME" />
              </span>
              <input
                type="text"
                className={`${styles.user_input} ${styles.common_bg}`}
                maxLength="25"
                placeholder={intl.formatMessage({
                  id: "ID_ASSET_NAME_INPUT_PLACEHOLDER",
                })}
                onChange={updateName}
              />
              <p className={styles.count_length}>{`${name.length}/25`}</p>
            </div>

            {/* <div className={styles.flex_row}>
              <span className={styles.category}>
                <FormattedMessage id="ID_CATEGORY_PUBLIC" />
              </span>
              <span className={`${styles.toggle_btn} ${isPublic ? `${styles.active}` : ""}`}>
                <span className={styles.toggle} />
              </span>
            </div> */}

            <div className={styles.flex_row}>
              <span className={styles.category}>
                <FormattedMessage id="ID_CATEGORY_KEYWORD" />
              </span>

              <div className={styles.keyword_input_container}>
                <div
                  placeholder={intl.formatMessage({
                    id: "ID_KEYWORD_PLACEHOLDER",
                  })}
                  className={`${styles.user_text} ${styles.common_bg}`}
                  onClick={focusKeywordInput}
                >
                  {keywordList.map((keyword, index) => (
                    <span className={styles.tag} key={index}>
                      {keyword}
                      <img alt="close" src={TagClose} onClick={() => onClickDeleteKeyword(keyword)} />
                    </span>
                  ))}
                  <input
                    type="text"
                    value={keyword}
                    maxLength="8"
                    className={styles.tag_input}
                    tabIndex={2}
                    onChange={onChangeKeyword}
                    onKeyDown={onKeydownKeyword}
                    ref={keywordInputRef}
                  />
                </div>
                <p className={styles.keyword_limit_notice}>{keywordList.length > 10 ? "keyword는 10개까지 등록 가능합니다." : ""}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.buttons}>
          <button className={styles.cancel} type="button" onClick={onClickCancel}>
            <FormattedMessage id="ID_ACTION_CANCEL" />
          </button>
          <button className={`${styles.save} ${isValid ? styles.active : ""}`} type="button" onClick={onClickUpload} disabled={!isValid}>
            <FormattedMessage id="ID_ACTION_SAVE" />
          </button>
        </div>
      </div>
      <ProgressBox percentage={uploadProgress} loaded={!uploadProgress} />
    </>
  );
}

export default SaveAsAssetPopup;
